import i18n from '@/i18n';
import { mapActions, mapGetters } from 'vuex';
import { ColvenTheme, ResetPasswordType, SnackbarStyle } from '@/constants/constants'
import { authService } from '@/services/auth.service';
import generalConfigurationApi from '@/api/general-configuration.api'

export default {
  name: 'Login',
  data() {
    return {
      validForm: false,
      showPassword: false,
      username: '',
      password: '',
      validationCode: '',
      showGoogleAutenticator: false,
      enterpriseId:0,
      remember: false,
      submitted: false,
      rules: {
        required: value => !!value || `${i18n.t('required')}`
      },
      languages: [
        {
          id: 'es',
          locale: 'es',
          name: i18n.t('login.spanish')
        },
        {
          id: 'us',
          locale: 'en',
          name: i18n.t('login.english')
        },
        {
          id: 'br',
          locale: 'pt',
          name: i18n.t('login.brazilian')
        }
      ],
      selectedLanguage: {
        id: 'es',
        locale: 'es',
        name: i18n.t('login.spanish')
      },
      systemVersion: null,
      loadVersion: false
    };
  },
  async mounted() {
    // Se elimina los datos de sesión
    this.clearSessionData()
    // Carga el locale almacenado
    if (this.getLocale) {
      switch (this.getLocale) {
        case 'es':
          this.selectedLanguage = this.languages[0]
          break
        case 'en':
          this.selectedLanguage = this.languages[1]
          break
        case 'pt':
          this.selectedLanguage = this.languages[2]
          break
        default:
          this.selectedLanguage = this.languages[0]
          break
      }
    } else {
      // const navigatorLanguage = navigator.language
      this.setLanguage(this.languages[0])
    }
    // remember
    if (this.getRemember && this.getUsername) {
      this.remember = this.getRemember
      this.username = this.getUsername
    } else {
      this.remember = false
      this.username = ''
    }
    if (!this.loadVersion) {
      const config = await generalConfigurationApi.getConfigSystemVersion('backoffice')
      if (config && config.data && config.data.version) {
        localStorage.setItem('systemVersion', config.data.version)
        this.systemVersion = config.data.version
      }
      this.loadVersion = true
    }
  },
  beforeDestroy () {
    // Carga los temas (si se logea un usuario diferente)
    if (this.getThemes) {
      this.$vuetify.theme.themes.light = this.getThemes.light
      this.$vuetify.theme.themes.dark = this.getThemes.dark
    } else {
      this.$vuetify.theme.themes.light = ColvenTheme.light
      this.$vuetify.theme.themes.dark = ColvenTheme.dark
    }
    this.$vuetify.theme.dark = this.getDark
  },
  computed: {
    ...mapGetters({
      'getDark': 'app/getDark',
      'getUsername': 'user/getUsername',
      'getPassword': 'user/getPassword',
      'getRemember': 'user/getRemember',
      'getLocale': 'user/getLocale',
      'getLogo': 'user/getLogo',
      'getLoginLogo': 'user/getLoginLogo',
      'getLoginBackgroundImage': 'user/getLoginBackgroundImage',
      'getThemes': 'user/getThemes',
    }),
    /**
     * src del logo
     */
    logoSource() {
      if (this.getLoginLogo) {
        return 'data:image/png;base64,' + this.getLoginLogo;
      } else {
        return require('../../assets/logo.png')
      }
    }
  },
  methods: {
    /**
     * actions de user
     */
    ...mapActions('user', {
      setUserId: 'setUserId',
      setUsername: 'setUsername',
      setPassword: 'setPassword',
      setRemember: 'setRemember',
      setEmail: 'setEmail',
      setLogo: 'setLogo',
      setLoginLogo: 'setLoginLogo',
      setLoginBackgroundImage: 'setLoginBackgroundImage',
      setThemes: 'setThemes',
      setLocale: 'setLocale',
      login: 'login',
      setResetPasswordJWT: 'setResetPasswordJWT',
      setToken: 'setToken',
      setPrivileges: 'setPrivileges',
      setAdmin: 'setAdmin',
      clearSessionData: 'clearSessionData',
      setEnterpriseId: 'setEnterpriseId'
    }),
    /**
     * actions de snackbar
     */
    ...mapActions({
      'showSnackbar': 'snackbar/showSnackbar',
      'closeSnackbar': 'snackbar/closeSnackbar'
    }),
    /**
     * Para cambiar el lenguaje de i18n
     * @param {*} lang
     */
    setLanguage (lang) {
      // Setea el lenguaje de los componentes de Vuetify
      this.$vuetify.lang.current = lang.locale
      this.selectedLanguage = lang
      this.$root.$i18n.locale = lang.locale
      this.setLocale(lang.locale)
    },
    /**
     * Redirecciona a la pantalla para recuperar contraseña
     */
    forgotPassword() {
      this.$router.push({ name: 'emailResetPassword' }).catch(err => {
        console.log(err);
      });
    },
    async login(){
      if(!this.showGoogleAutenticator)
      {
        try {
          // login
          let userSession = await authService.login({
            username: this.username,
            password: this.password
          })
  
          // remember
          this.setRemember(this.remember)
          this.setUsername(this.username)
          this.setPassword(this.password)
          this.setUserId(userSession.user.userId)
          this.setEmail(userSession.user.email)
          this.setToken(userSession.sessionToken)
          //si tiene googleAutenticatorSecret redirige a la ventana de seleccionar empresas para ingresar el token de google
          if(userSession.user.haveGoogleAutenticatorSecret){
            localStorage.setItem('haveGoogleAutenticatorSecret', true)
            this.showGoogleAutenticator = true;
          }
          else{
            localStorage.removeItem('haveGoogleAutenticatorSecret')
            this.showGoogleAutenticator = false;
          }
          if (userSession.enterprise) {
            // enterpriseId
            this.enterpriseId = userSession.enterprise._id;
            if(!this.showGoogleAutenticator){
              this.setEnterpriseId(this.enterpriseId)  
            }
            
            // themes
            this.setThemes(userSession.enterprise.themes)
            // logo
            this.setLogo(userSession.enterprise.logo)
            // login logo
            this.setLoginLogo(userSession.enterprise.loginLogo)
            // background image
            this.setLoginBackgroundImage(userSession.enterprise.loginBackgroundImage)
          }
  
          // Si el usuario debe recuperar la contraseña
          if (userSession.updatePasswordType) {
            authService.getResetPasswordToken({ username: this.username, password: this.password }).then(tokenResponse => {
              this.setResetPasswordJWT(tokenResponse.token)
              switch (userSession.updatePasswordType) {
                case ResetPasswordType.EXPIRATION:
                  this.$router.push('/reset-password/expiration/' + tokenResponse.token)
                  break
                case ResetPasswordType.NEXT_LOGIN:
                  this.$router.push('/reset-password/next-login/' + tokenResponse.token)
                  break
                default:
                  this.showSnackbar({ visible: true, text: i18n.t('login.invalidPasswordResetType'), timeout: 6000, style: SnackbarStyle.ERROR })
              }
            })
          } else {
            /*
              Configuraciones y privilegios
            */
            localStorage.setItem('userConfiguration', JSON.stringify(userSession.configurations))
            this.setPrivileges(userSession.user.privileges)
            this.setAdmin(userSession.user.superAdmin || false)
            if(!this.showGoogleAutenticator){
              this.$router.push('/app');  
            }
          }
        } catch (error) {
          console.log('Error in login action', error)
        }
      }
    },
    async submit() {
      if(this.showGoogleAutenticator)
      {
        const userSession = await authService.refreshTokenSecondFactor(this.enterpriseId,this.validationCode)
        this.setToken(userSession.sessionToken)
        if(userSession.enterprise)
        {
          this.setEnterpriseId(userSession.enterprise._id)  
        }
        
        this.$router.push('/app');
      }
      else{
        await this.login()
      }
    }
  }
};
